import { FormEvent } from "react"
import { Link, useForm, usePage } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"

import type { PageProps } from "@/types/inertia"

type DestinationFormData = {
  parent_id: string
  kind: string
  status: string
  name_en: string
  name_de: string
  slug_en: string
  slug_de: string
  intro_en: string
  intro_de: string
  summary_en: string
  summary_de: string
  meta_title_en: string
  meta_title_de: string
  meta_description_en: string
  meta_description_de: string
  latitude: string
  longitude: string
}

export type DestinationFormRecord = DestinationFormData & {
  id: number | null
}

export type ParentOption = {
  id: number
  label: string
}

type DestinationFormProps = {
  destination: DestinationFormRecord
  parentOptions: ParentOption[]
  kinds: string[]
  statuses: string[]
  action: string
  method: "post" | "patch"
  submitLabel: string
}

export function DestinationForm({
  destination,
  parentOptions,
  kinds,
  statuses,
  action,
  method,
  submitLabel,
}: DestinationFormProps) {
  const { props } = usePage<PageProps>()
  const form = useForm<DestinationFormData>({
    parent_id: destination.parent_id ?? "",
    kind: destination.kind ?? "town",
    status: destination.status ?? "draft",
    name_en: destination.name_en ?? "",
    name_de: destination.name_de ?? "",
    slug_en: destination.slug_en ?? "",
    slug_de: destination.slug_de ?? "",
    intro_en: destination.intro_en ?? "",
    intro_de: destination.intro_de ?? "",
    summary_en: destination.summary_en ?? "",
    summary_de: destination.summary_de ?? "",
    meta_title_en: destination.meta_title_en ?? "",
    meta_title_de: destination.meta_title_de ?? "",
    meta_description_en: destination.meta_description_en ?? "",
    meta_description_de: destination.meta_description_de ?? "",
    latitude: destination.latitude?.toString() ?? "",
    longitude: destination.longitude?.toString() ?? "",
  })

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (method === "patch") {
      form.patch(action)
    } else {
      form.post(action)
    }
  }

  return (
    <form onSubmit={submit} className="mt-6 space-y-8">
      <Section title="Publishing">
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Kind" error={props.errors?.kind}>
            <Select value={form.data.kind} onChange={(event) => form.setData("kind", event.target.value)}>
              {kinds.map((kind) => (
                <option key={kind} value={kind}>
                  {titleize(kind)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Status" error={props.errors?.status}>
            <Select value={form.data.status} onChange={(event) => form.setData("status", event.target.value)}>
              {statuses.map((status) => (
                <option key={status} value={status}>
                  {titleize(status)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Parent" error={props.errors?.parent_id}>
            <Select
              value={form.data.parent_id}
              onChange={(event) => form.setData("parent_id", event.target.value)}
            >
              <option value="">None</option>
              {parentOptions.map((parent) => (
                <option key={parent.id} value={parent.id}>
                  {parent.label}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      </Section>

      <Section title="Localized content">
        <div className="grid gap-4 lg:grid-cols-2">
          <LocalizedFields locale="en" form={form} errors={props.errors} />
          <LocalizedFields locale="de" form={form} errors={props.errors} />
        </div>
      </Section>

      <Section title="Center point">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Latitude" error={props.errors?.latitude}>
            <Input
              type="number"
              step="any"
              required
              value={form.data.latitude}
              onChange={(event) => form.setData("latitude", event.target.value)}
            />
          </Field>
          <Field label="Longitude" error={props.errors?.longitude}>
            <Input
              type="number"
              step="any"
              required
              value={form.data.longitude}
              onChange={(event) => form.setData("longitude", event.target.value)}
            />
          </Field>
        </div>
      </Section>

      <div className="flex items-center gap-3 border-t border-hairline pt-5">
        <Button type="submit" disabled={form.processing}>
          {submitLabel}
        </Button>
        <Button asChild variant="secondary">
          <Link href="/admin/destinations">Cancel</Link>
        </Button>
      </div>
    </form>
  )
}

function LocalizedFields({
  locale,
  form,
  errors,
}: {
  locale: "en" | "de"
  form: ReturnType<typeof useForm<DestinationFormData>>
  errors: Record<string, string>
}) {
  const suffix = locale.toUpperCase()

  return (
    <div className="space-y-4 rounded-md border border-hairline p-4">
      <h3>{suffix}</h3>
      <Field label="Name" error={errors?.[`name_${locale}`]}>
        <Input
          value={form.data[`name_${locale}`]}
          onChange={(event) => form.setData(`name_${locale}`, event.target.value)}
        />
      </Field>
      <Field label="Slug" error={errors?.[`slug_${locale}`]}>
        <Input
          value={form.data[`slug_${locale}`]}
          onChange={(event) => form.setData(`slug_${locale}`, event.target.value)}
        />
      </Field>
      <Field label="Intro" error={errors?.[`intro_${locale}`]}>
        <textarea
          className="form-control form-control-textarea"
          value={form.data[`intro_${locale}`]}
          onChange={(event) => form.setData(`intro_${locale}`, event.target.value)}
        />
      </Field>
      <Field label="Summary" error={errors?.[`summary_${locale}`]}>
        <textarea
          className="form-control form-control-textarea"
          value={form.data[`summary_${locale}`]}
          onChange={(event) => form.setData(`summary_${locale}`, event.target.value)}
        />
      </Field>
      <Field label="Meta title" error={errors?.[`meta_title_${locale}`]}>
        <Input
          value={form.data[`meta_title_${locale}`]}
          onChange={(event) => form.setData(`meta_title_${locale}`, event.target.value)}
        />
      </Field>
      <Field label="Meta description" error={errors?.[`meta_description_${locale}`]}>
        <textarea
          className="form-control form-control-textarea"
          value={form.data[`meta_description_${locale}`]}
          onChange={(event) => form.setData(`meta_description_${locale}`, event.target.value)}
        />
      </Field>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-lg">{title}</h2>
      <div className="mt-4">{children}</div>
    </section>
  )
}

function Field({
  label,
  error,
  children,
}: {
  label: string
  error?: string
  children: React.ReactNode
}) {
  return (
    <label className="space-y-2">
      <span>{label}</span>
      {children}
      {error && <span className="block text-sm text-danger-display">{error}</span>}
    </label>
  )
}

function titleize(value: string) {
  return value.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase())
}
